// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient global.d.ts has no runtime JS counterpart to import
/// <reference path="./global.d.ts" />
"use client";

import { logError } from "@jetblue/dotcom-fe-logger";
import {
  emit,
  whenUserConsentDecisionKnown,
  type RawDecision,
} from "./provider";
import { invalidateConsentDocuments } from "./synchronization";

type ConsentDecision = RawDecision;

type PrivacyManagerApiMessage = {
  PrivacyManagerAPI?: {
    capabilities?: object;
    action?: string;
  };
};

const PRIVACY_MANAGER_API_GLOBAL_KEY = "PrivacyManagerAPI";
const GET_GDPR_CONSENT_DECISION_METHOD = "getGDPRConsentDecision";
const GET_CONSENT_DECISION_MESSAGE_ACTION = "getConsentDecision";
const GDPR_PREF_ALLOWS_EVENT_PREFIX = "GDPR Pref Allows ";

const announcedGdprLabels = new Set<number>();

const dispatchGdprLabels = (labels: ConsentDecision) => {
  for (const label of labels) {
    if (!announcedGdprLabels.has(label)) {
      globalThis.dataLayer?.push({
        event: GDPR_PREF_ALLOWS_EVENT_PREFIX + String(label),
      });
      announcedGdprLabels.add(label);
    }
  }

  for (const label of announcedGdprLabels) {
    if (!labels.includes(label)) {
      announcedGdprLabels.delete(label);
    }
  }
};

const handleConsentDecision = (decision: ConsentDecision) => {
  dispatchGdprLabels(decision);

  const emission = emit(decision);
  if (emission === "changed") {
    invalidateConsentDocuments();
  }
};

let consentResolutionPromise: Promise<void> | undefined;

let trustedConsentOrigin: string | undefined;

export const setTrustedConsentOrigin = (origin: string) => {
  trustedConsentOrigin = origin;
};

const isAcceptedConsentMessageOrigin = (origin: string) =>
  origin === globalThis.location.origin || origin === trustedConsentOrigin;

const readAssertedConsentDecision = () => {
  const { consentDecision, source } =
    globalThis.PrivacyManagerAPI?.callApi(
      GET_GDPR_CONSENT_DECISION_METHOD,
      globalThis.location.host,
    ) ?? {};

  return source === "asserted" && consentDecision ? consentDecision : undefined;
};

const parseConsentMessage = (data: unknown) => {
  if (typeof data !== "string") {
    return;
  }

  try {
    return JSON.parse(data) as PrivacyManagerApiMessage;
  } catch {
    // The page receives postMessages from unrelated scripts. Anything that is
    // not JSON is simply not ours, so this is expected rather than a failure.
    return;
  }
};

const handleConsentMessage = (event: MessageEvent) => {
  if (!isAcceptedConsentMessageOrigin(event.origin)) {
    return;
  }

  const parsed = parseConsentMessage(event.data);
  if (
    !parsed?.PrivacyManagerAPI?.capabilities ||
    parsed.PrivacyManagerAPI.action !== GET_CONSENT_DECISION_MESSAGE_ACTION
  ) {
    return;
  }

  try {
    const decision = readAssertedConsentDecision();
    if (decision) {
      handleConsentDecision(decision);
    }
  } catch (error) {
    // A subscriber threw while reacting to a consent change, which means a
    // vendor may not have started or stopped. Never silent: this is the only
    // place it surfaces.
    void logError({
      error,
      message: "Failed to publish a TrustArc consent change",
    });
  }
};

let consentChangeListenerRegistered = false;

export const registerConsentChangeListener = () => {
  if (
    consentChangeListenerRegistered ||
    typeof globalThis.addEventListener !== "function"
  ) {
    return;
  }

  consentChangeListenerRegistered = true;
  globalThis.addEventListener("message", handleConsentMessage);
};

const PRIVACY_MANAGER_API_POLL_MAX_CHECKS = 40;
const PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS = 100;
const PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS = 1000;
const PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS = 10;
const PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR = 1.3;

const getPrivacyManagerApiPollIntervalMs = (checkNumber: number) => {
  const checksPastPlateau =
    checkNumber - PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS;
  if (checksPastPlateau <= 0) {
    return PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS;
  }

  return Math.min(
    PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS,
    PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS *
      PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR ** checksPastPlateau,
  );
};

const pollUntilPrivacyManagerApiIsAvailable = (
  maxChecks = PRIVACY_MANAGER_API_POLL_MAX_CHECKS,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
      resolve();
      return;
    }

    let checks = 0;
    const scheduleNextCheck = () => {
      setTimeout(() => {
        checks++;
        if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
          resolve();
        } else if (checks >= maxChecks) {
          reject(new Error("PrivacyManagerAPI never became available"));
        } else {
          scheduleNextCheck();
        }
      }, getPrivacyManagerApiPollIntervalMs(checks));
    };

    scheduleNextCheck();
  });

export const resolveConsentDecision = (): Promise<void> => {
  consentResolutionPromise ??= pollUntilPrivacyManagerApiIsAvailable()
    .catch((error: unknown) => {
      handleConsentDecision([]);
      consentResolutionPromise = undefined;
      throw error;
    })
    .then(() => {
      const existingDecision = readAssertedConsentDecision();

      if (existingDecision && existingDecision.length > 0) {
        handleConsentDecision(existingDecision);
      }

      globalThis.postMessage(
        JSON.stringify({
          PrivacyManagerAPI: {
            action: GET_CONSENT_DECISION_MESSAGE_ACTION,
            self: globalThis.location.host,
            timestamp: Date.now(),
          },
        }),
        globalThis.location.origin,
      );

      return whenUserConsentDecisionKnown();
    });

  return consentResolutionPromise;
};
